import { supabase } from '../config/supabase';
import type { Expense, ExpenseCategory } from '../types/expense';

export const getAllExpenses = async (): Promise<Expense[]> => {
  const { data, error } = await supabase
    .from('expenses')
    .select('*')
    .order('date', { ascending: false });

  if (error) {
    console.error('Error fetching expenses:', error);
    return [];
  }
  return data.map(mapExpenseFromDB);
};

export const createExpense = async (
  data: Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Expense | null> => {
  const { data: result, error } = await supabase.from('expenses').insert({
    amount: data.amount,
    currency: data.currency,
    category: data.category,
    merchant: data.merchant,
    note: data.note,
    date: data.date,
  }).select().single();

  if (error) {
    console.error('Error creating expense:', error);
    return null;
  }
  return mapExpenseFromDB(result);
};

export const updateExpense = async (
  id: string,
  updates: Partial<Omit<Expense, 'id' | 'createdAt'>>
): Promise<Expense | null> => {
  const dbUpdates: any = {};
  if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
  if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
  if (updates.category !== undefined) dbUpdates.category = updates.category;
  if (updates.merchant !== undefined) dbUpdates.merchant = updates.merchant;
  if (updates.note !== undefined) dbUpdates.note = updates.note;
  if (updates.date !== undefined) dbUpdates.date = updates.date;
  dbUpdates.updated_at = new Date().toISOString();

  const { data, error } = await supabase.from('expenses').update(dbUpdates).eq('id', id).select().single();

  if (error) {
    console.error('Error updating expense:', error);
    return null;
  }
  return mapExpenseFromDB(data);
};

export const deleteExpense = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from('expenses').delete().eq('id', id);
  if (error) {
    console.error('Error deleting expense:', error);
    return false;
  }
  return true;
};

export const groupByDay = (expenses: Expense[]): Map<string, Expense[]> => {
  const map = new Map<string, Expense[]>();
  const sorted = [...expenses].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  for (const e of sorted) {
    const existing = map.get(e.date) ?? [];
    map.set(e.date, [...existing, e]);
  }
  return map;
};

export const groupByMonth = (expenses: Expense[]): Map<string, Expense[]> => {
  const map = new Map<string, Expense[]>();
  for (const e of expenses) {
    const key = e.date.substring(0, 7);
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, e]);
  }
  return map;
};

export interface SpendingStats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  lastMonth: number;
  categoryBreakdown: Record<ExpenseCategory, number>;
  dailyAvgThisMonth: number;
}

export const getSpendingStats = (expenses: Expense[]): SpendingStats => {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const thisMonthKey = todayStr.substring(0, 7);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = lastMonthDate.toISOString().substring(0, 7);

  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - mondayOffset);
  const weekStartStr = weekStart.toISOString().split('T')[0];

  let today = 0;
  let thisWeek = 0;
  let thisMonth = 0;
  let lastMonth = 0;
  const categoryBreakdown: Partial<Record<ExpenseCategory, number>> = {};

  for (const e of expenses) {
    if (e.date === todayStr) today += e.amount;
    if (e.date >= weekStartStr && e.date <= todayStr) thisWeek += e.amount;
    if (e.date.startsWith(thisMonthKey)) {
      thisMonth += e.amount;
      categoryBreakdown[e.category] = (categoryBreakdown[e.category] ?? 0) + e.amount;
    }
    if (e.date.startsWith(lastMonthKey)) lastMonth += e.amount;
  }

  const daysElapsedInMonth = now.getDate();
  const dailyAvgThisMonth = daysElapsedInMonth > 0 ? thisMonth / daysElapsedInMonth : 0;

  return {
    today,
    thisWeek,
    thisMonth,
    lastMonth,
    categoryBreakdown: categoryBreakdown as Record<ExpenseCategory, number>,
    dailyAvgThisMonth,
  };
};

const mapExpenseFromDB = (row: any): Expense => ({
  id: row.id,
  amount: Number(row.amount),
  currency: row.currency,
  category: row.category,
  merchant: row.merchant,
  note: row.note,
  date: row.date,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});
